/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */
import React, { useState, useCallback, useMemo } from 'react';
import EnvelopesIcon from '../../../images/envelopes.svg';
import CONFIG from '../../../config';
import { theme } from './theme';
import { useAppSelector } from '../../../reducers';
import MagicBell, { NotificationList, useNotifications } from '@magicbell/magicbell-react';
import { Button, Input, FormGroup, Label } from 'reactstrap';
import { naturalSort } from '../../../utils';
import { Loader } from '@g17eco/atoms/loader';
import { NotificationPreferencesUpdateRequest, NotificationServices, NotificationChannels } from '../../../types/notifications';
import { deleteAllNotifications } from './magicbell-notifications';
import { Checkbox, CheckboxState } from '@g17eco/atoms/checkbox';
import './styles.scss';
import { getCategoryGroupLabel, getCategoryLabel } from '@utils/notifications';
import { generateToast } from '@g17eco/molecules/toasts';
import { useToggle } from '@hooks/useToggle';
import { useGetMagicBellTokenQuery, useGetNotificationPreferencesQuery, useSetNotificationPreferencesMutation, useUpdateNotificationSummariesMutation } from '@api/users';
import { TimePeriod } from '@utils/date';
import { BasicAlert } from '@g17eco/molecules/alert';
import { useChatbotVisibilityControl } from '@hooks/useChatbotVisibilityControl';
import { FeatureStability } from '@g17eco/molecules/feature-stability';

interface NotificationsWidgetProps {
  launcherRef: React.RefObject<Element>;
  isOpen: boolean;
  toggle: () => void;
  userKey: string;
  userExternalId: string | undefined;
}

type NotificationStore = ReturnType<typeof useNotifications>;

interface NotificationsInboxProps {
  store: NotificationStore | null;
}

const NotificationsInbox = (props: NotificationsInboxProps) => {
  if (props.store?.notifications && props.store.notifications.length === 0) {
    return (
      <div className='notifications-inbox h-100 d-flex flex-column justify-content-center text-center'>
        <div>
          <img src={EnvelopesIcon} alt='No notifications' />
        </div>
        <div className='mt-3'>
          You have no recent notifications
        </div>
      </div>
    );
  }

  return (
    <div className='notifications-inbox'>
      {props.store ? <NotificationList height={440} notifications={props.store} /> : <>Unable to get notifications</>}
    </div>
  );
}

interface NotificationPreferencesCategory {
  code: string;
  label: string;
  in_app: boolean;
  email: boolean;
  group: string;
}

interface CategoryGroup {
  group: string;
  channel: NotificationChannels;
}

const NotificationsPreferences = () => {
  const { data: preferences, isFetching } = useGetNotificationPreferencesQuery();
  const [setNotificationPreferences, { isLoading: setPreferencesLoading, error: setPreferencesError }] = useSetNotificationPreferencesMutation();
  const [updateNotificationSummaries, { isLoading: updateSummariesLoading, error: updateSummariesError }] = useUpdateNotificationSummariesMutation();

  const categories = useMemo<NotificationPreferencesCategory[]>(() => {
    if (!preferences?.magicBell.notification_preferences) {
      return [];
    }

    const categoryPreferences = preferences.magicBell.notification_preferences.categories;
    const groups = preferences.magicBell.notification_preferences.groups;
    const categories = groups
      .map((group) =>
        Object.keys(categoryPreferences)
          .filter((category) => categoryPreferences[category].group === group)
          .sort(naturalSort)
          .map((category) => ({
            code: category,
            label: getCategoryLabel(category),
            in_app: categoryPreferences[category].in_app ?? false,
            email: categoryPreferences[category].email ?? false,
            group: categoryPreferences[category].group,
          }))
      )
      .flat();
    return categories;
  }, [preferences]);

  const isSummary = Boolean(preferences?.notificationSchedule?.isSummary);

  const togglePreference = (category: string | string[], channel: NotificationChannels, setting: boolean) => {
    const preferences: NotificationPreferencesUpdateRequest = {
      service: NotificationServices.MAGIC_BELL,
      category,
      channel,
      setting,
    };
    setNotificationPreferences(preferences);
  };

  const toggleSummaries = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.currentTarget.checked;
    updateNotificationSummaries({ period: TimePeriod.Weekly, isSummary: checked });
  }

  const toggleGroup = ({ group, channel }: CategoryGroup) => {
    const status = getGroupStatus({ group, channel });

    const categories = getCategoriesByGroup(group).map((category) => category.code);
    togglePreference(categories, channel, status !== CheckboxState.Checked);
  };

  const getCategoriesByGroup = (group: string) => categories.filter((category) => category.group === group);

  const isGroupChecked = ({ group, channel }: CategoryGroup) =>
    getCategoriesByGroup(group).every((category) => category[channel]);

  const isGroupUnchecked = ({ group, channel }: CategoryGroup) =>
    getCategoriesByGroup(group).every((category) => !category[channel]);

  const getGroupStatus = ({ group, channel }: CategoryGroup) =>
    isGroupChecked({ group, channel })
      ? CheckboxState.Checked
      : isGroupUnchecked({ group, channel })
      ? CheckboxState.Unchecked
      : CheckboxState.Indeterminate;

  if (setPreferencesError?.message || updateSummariesError?.message) {
    return (
      <div className='notifications-preferences'>
        <BasicAlert type={'danger'}>{setPreferencesError?.message || updateSummariesError?.message}</BasicAlert>
      </div>
    )
  }

  if (isFetching || setPreferencesLoading || updateSummariesLoading) {
    return <Loader />;
  }

  const groups = categories.reduce(
    (prev, curr) => (prev.includes(curr.group) ? prev : [...prev, curr.group]),
    [] as string[]
  );

  return (
    <div className='notifications-preferences'>
      <div className='d-flex justify-content-between align-items-center mb-3'>
        <div className='flex-grow-1'>
          <p className='m-0'><FeatureStability stability={'beta'} className='mr-1' />Weekly email summaries</p>
          <p className='m-0 text-xs'>Receive summary emails weekly rather then immediately</p>
        </div>
        <div className='flex-grow-0 pl-3'>
          <FormGroup switch className='d-flex'>
            <Input id={'notification-summaries'} type='switch' checked={isSummary} onChange={toggleSummaries} />
            <label htmlFor={'notification-summaries'} className='text-md'></label>
          </FormGroup>
        </div>
      </div>
      <table width='100%' className='notifications-preferences__table'>
        <thead>
          <tr>
            <th />
            <th className='text-strong text-ThemeTextDark'>PLATFORM</th>
            <th className='text-strong text-ThemeTextDark'>E-MAIL</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <React.Fragment key={group}>
              <tr className='notifications-preferences__group'>
                <td className='text-left text-strong text-ThemeTextDark'>
                  <Label>{getCategoryGroupLabel(group)}</Label>
                </td>
                <td>
                  <FormGroup check inline className='mx-0'>
                    <Checkbox
                      status={getGroupStatus({ group, channel: NotificationChannels.IN_APP })}
                      code={group}
                      onChange={() => toggleGroup({ group, channel: NotificationChannels.IN_APP })}
                    />
                  </FormGroup>
                </td>
                <td>
                  <FormGroup check inline className='mx-0'>
                    <Checkbox
                      status={getGroupStatus({ group, channel: NotificationChannels.EMAIL })}
                      code={group}
                      onChange={() => toggleGroup({ group, channel: NotificationChannels.EMAIL })}
                    />
                  </FormGroup>
                </td>
              </tr>
              {getCategoriesByGroup(group).map((category) => (
                <tr key={category.code}>
                  <td className='text-left text-ThemeTextDark'>
                    <Label>{category.label}</Label>
                  </td>
                  <td>
                    <FormGroup check inline className='mx-0'>
                      <Input
                        type='checkbox'
                        code={category.code}
                        checked={category.in_app}
                        onChange={() => togglePreference(category.code, NotificationChannels.IN_APP, !category.in_app)}
                      />
                    </FormGroup>
                  </td>
                  <td>
                    <FormGroup check inline className='mx-0'>
                      <Input
                        type='checkbox'
                        code={category.code}
                        checked={category.email}
                        onChange={() => togglePreference(category.code, NotificationChannels.EMAIL, !category.email)}
                      />
                    </FormGroup>
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const NotificationsWidget = (props: NotificationsWidgetProps) => {
  useChatbotVisibilityControl({ isHidden: props.isOpen });

  const [isDeleting, toggleIsDeleting] = useToggle(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const store = useNotifications();
  const markAsRead = () => store?.markAllAsRead();
  const togglePreferences = () => setShowPreferences(!showPreferences);

  const deleteAll = useCallback(() => {
    toggleIsDeleting();
    deleteAllNotifications(props.userKey, props.userExternalId)
      .then(async () => {
        await store?.fetch({ page: 1 }, { reset: true });
        generateToast({
          color: 'success',
          message: 'All notifications deleted',
        });
      })
      .catch(() =>
        generateToast({
          color: 'danger',
          message: 'Failed to delete notifications',
        })
      ).finally(() => toggleIsDeleting());
  }, [props.userExternalId, props.userKey, store, toggleIsDeleting]);

  return (
    <>
      <div className='notifications-arrow'></div>
      <div className='notifications-background' onClick={() => props.toggle()}></div>
      <div className='notifications-container'>
        <div className='notifications-header d-flex align-items-center'>
          <div className='h5 text-ThemeTextWhite flex-fill'>{showPreferences ? 'PREFERENCES' : 'NOTIFICATIONS'}</div>
          <div>
            {showPreferences
              ?
              <Button color='link' className='p-0' onClick={togglePreferences}><i className='fas fa-times' /></Button>
              :
              <>
                {isDeleting ? (
                  <Button color='link' disabled className='p-0 mr-3'>
                    Deleting
                  </Button>
                ) : (
                  <Button color='link' className='p-0 mr-3' onClick={deleteAll}>
                    Delete All
                  </Button>
                )}
                <Button color='link' className='p-0' onClick={markAsRead}>
                  Mark All Read
                </Button>
              </>
            }
          </div>
        </div>
        <div className='notifications-body'>
          {showPreferences ? <NotificationsPreferences /> : <NotificationsInbox store={store} />}
        </div>
        <div className='notifications-footer'>
          <Button color='link' size='lg' onClick={togglePreferences}>
            <i className='text-ThemeTextWhite fal fa-cog' />
          </Button>
        </div>
      </div>
    </>
  );
}

const Notifications = () => {
  const currentUser = useAppSelector((state) => state.currentUser);
  const loggedIn = currentUser.loaded;
  const { data: authCode } = useGetMagicBellTokenQuery(undefined, { skip: !loggedIn });

  if (!authCode) {
    return null;
  }

  if (!currentUser.data._id) {
    return null;
  }

  return <MagicBell
    apiKey={CONFIG.magicBellApiKey}
    userExternalId={currentUser.data._id}
    userKey={authCode}
    theme={theme}
    images={{
      emptyInboxUrl: EnvelopesIcon
    }}
    locale={{
      name: 'en-US',
      translations: {
        messages: {
          'empty-inbox': 'You have no new notifications.',
        },
      },
    }}
  >
    {
      (props) => <NotificationsWidget {...props} userKey={authCode} userExternalId={currentUser.data._id} />
    }
  </MagicBell>
};

export default Notifications;
